import React, { useMemo } from 'react';
import { BodyText, Button, PhoneIcon, Stack } from '@kvdbil/components';
import dayjs from 'dayjs';
import { OpeningHour } from '~/App/shared/interfaces/OpeningHours';
import { TranslateFunction, useTranslation } from '~/Locale';
import {
  formatPhoneNumber,
  formatPhoneNumberForLink
} from '~/helpers/phoneNumber';

export const supportDateFormat = 'YYYY-MM-DD';

const getNextOpenTime = (openingHours: OpeningHour[]) => {
  const today = dayjs().format(supportDateFormat);
  const nextOpeningHours = openingHours.find(openingHour =>
    dayjs(openingHour.date).isAfter(today)
  );
  if (nextOpeningHours) {
    return nextOpeningHours;
  }
  return null;
};

export const isOpenToday = (openingHours: OpeningHour[]) => {
  const today = dayjs().format(supportDateFormat);
  const todayOpeningHours = openingHours.find(
    openingHour => openingHour.date.format(supportDateFormat) === today
  );

  if (!todayOpeningHours) {
    return {
      isOpen: false,
      nextOpenTime: getNextOpenTime(openingHours)
    };
  }

  if (todayOpeningHours.type === 'CLOSED') {
    return {
      isOpen: false,
      nextOpenTime: getNextOpenTime(openingHours)
    };
  }
  const now = dayjs();
  const openTime = dayjs(todayOpeningHours.open, 'HH:mm');
  const closeTime = dayjs(todayOpeningHours.close, 'HH:mm');

  if (now.isBefore(openTime) || now.isAfter(closeTime)) {
    return {
      isOpen: false,
      nextOpenTime: getNextOpenTime(openingHours)
    };
  }

  return {
    isOpen: true,
    openTime: openTime.format('HH:mm'),
    closeTime: closeTime.format('HH:mm')
  };
};

export type SupportHours = ReturnType<typeof isOpenToday>;

const getOpenString = (hours: SupportHours | null, t: TranslateFunction) => {
  if (!hours) {
    return null;
  }

  if (hours.isOpen) {
    return `${t('Open')} (${t('today')}) ${hours.openTime} - ${
      hours.closeTime
    }`;
  } else if (hours.nextOpenTime?.date && hours.nextOpenTime?.open) {
    return `${t('Support is closed.')} ${t(
      'Opens again on'
    )} ${hours.nextOpenTime?.date.format(
      'dddd'
    )} ${hours.nextOpenTime?.open?.format(
      'HH:mm'
    )} (${hours.nextOpenTime?.date.format('D MMM')})`;
  } else {
    return `${t('Support is closed.')}`;
  }
};

interface Props {
  title?: string;
  showSupportHours?: boolean;
  setSupportOpen?: (open: boolean) => void;
  hours: SupportHours | null;
}

const CallSupport = ({ title, showSupportHours = false, hours }: Props) => {
  const { t } = useTranslation();

  const openString = useMemo(
    () => showSupportHours && getOpenString(hours, t),
    [hours, t, showSupportHours]
  );
  return (
    <Stack>
      {title && <BodyText>{title}</BodyText>}

      <Button
        style={{ justifyContent: 'center' }}
        isDisabled={!hours?.isOpen}
        prefixIcon={<PhoneIcon />}
        variant="outline"
        color="info"
        colorShade="dark"
        size="large"
        onClick={() => {
          window.open(
            `tel:${formatPhoneNumberForLink(t('CUSTOMER_CONTACT_PHONE'))}`
          );
        }}
      >
        {formatPhoneNumber(t('CUSTOMER_CONTACT_PHONE'))}
      </Button>

      {showSupportHours && openString && <BodyText>{openString}</BodyText>}
    </Stack>
  );
};

export default CallSupport;
